import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';
import type { AppThunk } from '@/redux/common/store';

// ─── Response envelopes (API Response Standard) ────────────────────────────────
interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}
interface ApiListResponse<T> extends ApiResponse<T[]> {
  total: number;
}

// ─── Domain types (VIN-FEAT-03) ────────────────────────────────────────────────
export type NoteStatus =
  | 'draft'
  | 'recording'
  | 'transcribing'
  | 'analyzing'
  | 'ready'
  | 'saved'
  | 'discarded'
  | 'failed';

export type NoteContextType =
  | 'property'
  | 'lead'
  | 'contact'
  | 'assessment'
  | 'transaction'
  | 'meeting';

export interface NoteContextLink {
  context_type: NoteContextType | string;
  entity_id: string;
  display_label?: string | null;
}

export interface NoteEntity {
  type: string;
  value: string;
}

export type ConvertTarget = 'task' | 'property_note' | 'estimate_input' | 'proposal_comment' | 'draft_communication' | 'link_entity';

export interface NoteSuggestion {
  suggestion_id: string;
  type: 'convert' | 'create_task' | 'link_entity' | 'follow_up' | string;
  label: string;
  target?: ConvertTarget | string | null;
  confidence?: number | null;
  payload: Record<string, unknown>;
  confirmed: boolean;
  dismissed?: boolean;
  linked_record?: Record<string, unknown> | null;
}

export interface AnalysisMeta {
  model?: string | null;
  prompt_version?: string | null;
  analysis_version?: string | null;
  generated_at?: string | null;
  source?: string | null;
}

export interface Note {
  note_id: string;
  owner_user_id: string;
  source: 'voice' | 'text' | string;
  status: NoteStatus;
  title?: string | null;
  note_type: string;
  custom_tags: string[];
  context?: NoteContextLink | null;
  agent_title?: string | null;
  audio_url?: string | null;
  final_transcript?: string | null;
  clean_transcript?: string | null;
  summary?: string | null;
  review_summary?: string | null;
  follow_up_summary?: string | null;
  suggested_classifications: string[];
  entities: NoteEntity[];
  suggestions: NoteSuggestion[];
  linked_records: Record<string, unknown>[];
  meeting_note_id?: string | null;
  analysis_meta?: AnalysisMeta | null;
  transcript_hash?: string | null;
  analysis_stale?: boolean;
  created_on: string;
  updated_on: string;
}

/** Stateless analysis result (screen A → B); held in client state until Save. */
export interface AnalyzeDraftResult {
  clean_transcript?: string | null;
  agent_title?: string | null;
  review_summary?: string | null;
  follow_up_summary?: string | null;
  suggested_classifications: string[];
  entities: NoteEntity[];
  suggestions: NoteSuggestion[];
  analysis_meta?: AnalysisMeta | null;
}

export interface CreateNoteBody {
  content?: string;
  final_transcript?: string;
  clean_transcript?: string;
  audio_url?: string;
  source?: 'voice' | 'text';
  note_type?: string;
  custom_tags?: string[];
  context?: NoteContextLink;
  // Analysis outputs carried from screen B (one AI pass; not re-run on save).
  title?: string;
  agent_title?: string;
  review_summary?: string;
  follow_up_summary?: string;
  suggested_classifications?: string[];
  entities?: NoteEntity[];
  suggestions?: NoteSuggestion[];
  analysis_meta?: AnalysisMeta;
  idempotency_key?: string;
}

export interface UpdateNoteBody {
  title?: string;
  final_transcript?: string;
  clean_transcript?: string;
  summary?: string;
  audio_url?: string;
  note_type?: string;
  custom_tags?: string[];
  context?: NoteContextLink;
}

export interface ConvertTargetInfo {
  target: ConvertTarget | string;
  label: string;
  enabled: boolean;
  coming_soon_label?: string | null;
}

export interface ConfidenceThresholds {
  high: number;
  medium: number;
}

/** note_taker catalogue values the UI needs (classification chips, confidence bands). */
export interface NoteConfig {
  classification_tags: string[];
  confidence_thresholds: ConfidenceThresholds;
  note_context_types: string[];
  default_convert_target: string;
}

const DEFAULT_NOTE_CONFIG: NoteConfig = {
  classification_tags: [
    'customer_requirement', 'decision', 'follow_up', 'measurement',
    'risk', 'scope_change', 'site_condition', 'general',
  ],
  confidence_thresholds: { high: 0.8, medium: 0.5 },
  note_context_types: ['meeting', 'property', 'transaction', 'contact'],
  default_convert_target: 'task',
};

export interface NoteTraceability {
  note: Note;
  upstream: {
    source?: string | null;
    audio_url?: string | null;
    context?: NoteContextLink | null;
    meeting_note_id?: string | null;
    created_on?: string | null;
  };
  downstream: Record<string, unknown>[];
  timeline: {
    activity_type: string;
    actor_user_id: string;
    actor_name?: string | null;
    detail?: Record<string, unknown> | null;
    occurred_at: string;
  }[];
  analysis?: AnalysisMeta | null;
}

export const notesApi = createApi({
  reducerPath: 'notesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Note'],
  endpoints: (builder) => ({
    getNotes: builder.query<
      Note[],
      {
        mine?: boolean;
        status?: string;
        context_type?: string;
        context_entity_id?: string;
        search?: string;
        created_from?: string;
        created_to?: string;
      } | void
    >({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg) {
          Object.entries(arg).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
          });
        }
        const qs = params.toString();
        return `/api/v1/notes${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (res: ApiListResponse<Note>) => res.data,
      providesTags: ['Note'],
    }),

    getNote: builder.query<Note, string>({
      query: (id) => `/api/v1/notes/${id}`,
      transformResponse: (res: ApiResponse<Note>) => res.data,
      providesTags: ['Note'],
    }),

    createNote: builder.mutation<Note, CreateNoteBody>({
      query: (body) => ({ url: '/api/v1/notes', method: 'POST', body }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    updateNote: builder.mutation<Note, { id: string; body: UpdateNoteBody }>({
      query: ({ id, body }) => ({ url: `/api/v1/notes/${id}`, method: 'PATCH', body }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    uploadNoteAudio: builder.mutation<Note, { id: string; file: File }>({
      query: ({ id, file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `/api/v1/notes/${id}/audio`, method: 'POST', body: form };
      },
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    /** Screen A → B: one stateless AI pass on the captured transcript. Persists nothing. */
    analyzeDraft: builder.mutation<
      AnalyzeDraftResult,
      { transcript: string; context_hint?: NoteContextLink }
    >({
      query: (body) => ({ url: '/api/v1/notes/analyze', method: 'POST', body }),
      transformResponse: (res: ApiResponse<AnalyzeDraftResult>) => res.data,
    }),

    /** Screen B "Regenerate": re-run analysis on a SAVED note, clearing the stale flag. */
    regenerateAnalysis: builder.mutation<Note, string>({
      query: (id) => ({ url: `/api/v1/notes/${id}/regenerate-analysis`, method: 'POST' }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    analyzeNote: builder.mutation<Note, string>({
      query: (id) => ({ url: `/api/v1/notes/${id}/analyze`, method: 'POST' }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    // Retained for the saved-note editor; NOT part of the capture/review flow.
    appendNote: builder.mutation<Note, { id: string; content: string }>({
      query: ({ id, content }) => ({ url: `/api/v1/notes/${id}/append`, method: 'POST', body: { content } }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    saveNote: builder.mutation<Note, string>({
      query: (id) => ({ url: `/api/v1/notes/${id}/save`, method: 'POST' }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    discardNote: builder.mutation<Note, string>({
      query: (id) => ({ url: `/api/v1/notes/${id}/discard`, method: 'POST' }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    confirmSuggestion: builder.mutation<
      Note,
      { id: string; suggestionId: string; overrides?: Record<string, unknown> }
    >({
      query: ({ id, suggestionId, overrides }) => ({
        url: `/api/v1/notes/${id}/suggestions/${suggestionId}/confirm`,
        method: 'POST',
        body: { overrides: overrides ?? null },
      }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    dismissSuggestion: builder.mutation<Note, { id: string; suggestionId: string }>({
      query: ({ id, suggestionId }) => ({
        url: `/api/v1/notes/${id}/suggestions/${suggestionId}/dismiss`,
        method: 'POST',
      }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    deleteNote: builder.mutation<void, string>({
      query: (id) => ({ url: `/api/v1/notes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Note'],
    }),

    // ── Convert (screen E) + Traceability (screen G) ──────────────────────────
    getConvertTargets: builder.query<ConvertTargetInfo[], void>({
      query: () => '/api/v1/notes/convert-targets',
      transformResponse: (res: ApiResponse<ConvertTargetInfo[]>) => res.data,
    }),

    convertNoteItem: builder.mutation<
      Note,
      { id: string; suggestionId: string; target?: ConvertTarget | string; fields: Record<string, unknown> }
    >({
      query: ({ id, suggestionId, target, fields }) => ({
        url: `/api/v1/notes/${id}/convert`,
        method: 'POST',
        body: { suggestion_id: suggestionId, target, fields },
      }),
      transformResponse: (res: ApiResponse<Note>) => res.data,
      invalidatesTags: ['Note'],
    }),

    getTraceability: builder.query<NoteTraceability, string>({
      query: (id) => `/api/v1/notes/${id}/traceability`,
      transformResponse: (res: ApiResponse<NoteTraceability>) => res.data,
      providesTags: ['Note'],
    }),

    /** note_taker catalogue → classification chips + confidence bands (screens B/D). */
    getNoteConfig: builder.query<NoteConfig, void>({
      query: () => '/api/v1/configurations/note_taker',
      transformResponse: (res: { data?: { effective_value?: Record<string, unknown> } }) => {
        const v = res.data?.effective_value || {};
        return {
          classification_tags:
            (v.classification_tags as string[]) || DEFAULT_NOTE_CONFIG.classification_tags,
          confidence_thresholds:
            (v.confidence_thresholds as ConfidenceThresholds) || DEFAULT_NOTE_CONFIG.confidence_thresholds,
          note_context_types:
            (v.note_context_types as string[]) || DEFAULT_NOTE_CONFIG.note_context_types,
          default_convert_target:
            (v.default_convert_target as string) || DEFAULT_NOTE_CONFIG.default_convert_target,
        };
      },
    }),
  }),
});

export const {
  useGetNotesQuery,
  useGetNoteQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useUploadNoteAudioMutation,
  useAnalyzeDraftMutation,
  useRegenerateAnalysisMutation,
  useAnalyzeNoteMutation,
  useAppendNoteMutation,
  useSaveNoteMutation,
  useDiscardNoteMutation,
  useConfirmSuggestionMutation,
  useDismissSuggestionMutation,
  useDeleteNoteMutation,
  useGetConvertTargetsQuery,
  useConvertNoteItemMutation,
  useGetTraceabilityQuery,
  useGetNoteConfigQuery,
} = notesApi;

/** Invalidates all cached notes — dispatch this to trigger a fresh fetch for any mounted
 *  notes list or detail query. Used by the workspace Refresh button for the notes module. */
export const refreshNotesThunk = (_id?: string | null): AppThunk => (dispatch) => {
  dispatch(notesApi.util.invalidateTags(['Note']));
};
